import type { UseFormSetValue, FieldValues } from "react-hook-form";
import {
  searchReversedGeoLocationApiAction,
  getPortfolioByIdAction,
} from "@/components/modules/portfolio/api/actions/actions";
import type {
  BusinessUnitsSchemaType,
  GetRealEstateManagementSchemaType,
} from "@/components/modules/portfolio/api/schemas/schemas";

/**
 * Populate the new-room-listing form from a chosen portfolio room. Shared by
 * the inline PropertyAssociationSelect and the admin "create listing for this
 * property" flow so both prefill identically. Property images and location are
 * inherited for display only (never copied as listing fields).
 */
export async function applyUnitAssociation(
  setValue: UseFormSetValue<FieldValues>,
  {
    unit,
    portfolio,
  }: {
    unit: BusinessUnitsSchemaType;
    portfolio: GetRealEstateManagementSchemaType;
  },
) {
  setValue("associatedUnitId", unit.id, { shouldDirty: true });
  setValue("associatedUnitTitle", unit.title, { shouldDirty: true });
  setValue("associatedPortfolioTitle", portfolio.title, { shouldDirty: true });
  setValue("associatedPropertyImages", portfolio.imageUrls ?? [], {
    shouldDirty: true,
  });

  if (portfolio.latitude) {
    setValue("latitude", portfolio.latitude, { shouldDirty: true });
  }
  if (portfolio.longitude) {
    setValue("longitude", portfolio.longitude, { shouldDirty: true });
  }

  if (portfolio.description) {
    setValue("description", portfolio.description, { shouldDirty: true });
  }

  if (portfolio.bedrooms != null) {
    setValue("bedrooms", portfolio.bedrooms, { shouldDirty: true });
  }
  if (portfolio.bathrooms != null) {
    setValue("bathrooms", portfolio.bathrooms, { shouldDirty: true });
  }

  const FLAG_KEYS = ["petFriendly", "contract", "smokingAllowed"] as const;
  let houseFeatures = Array.isArray(portfolio.houseFeatures)
    ? (portfolio.houseFeatures as string[])
    : undefined;
  const flags: Record<(typeof FLAG_KEYS)[number], boolean | null | undefined> = {
    petFriendly: portfolio.petFriendly,
    contract: portfolio.contract,
    smokingAllowed: portfolio.smokingAllowed,
  };

  const needsFeatures = !houseFeatures || houseFeatures.length === 0;
  const needsFlags = FLAG_KEYS.some((k) => flags[k] == null);
  if ((needsFeatures || needsFlags) && portfolio.id) {
    try {
      const detail = (await getPortfolioByIdAction(portfolio.id))?.data;
      if (needsFeatures && Array.isArray(detail?.houseFeatures))
        houseFeatures = detail.houseFeatures as string[];
      for (const k of FLAG_KEYS) {
        if (flags[k] == null && detail?.[k] != null) flags[k] = detail[k];
      }
    } catch {
      /* empty */
    }
  }
  if (houseFeatures && houseFeatures.length > 0) {
    setValue("houseFeatures", houseFeatures, { shouldDirty: true });
  }
  for (const k of FLAG_KEYS) {
    if (flags[k] != null) setValue(k, flags[k], { shouldDirty: true });
  }

  if (portfolio.latitude && portfolio.longitude) {
    try {
      const result = await searchReversedGeoLocationApiAction({
        lat: Number(portfolio.latitude),
        lon: Number(portfolio.longitude),
      });
      const loc = result.data;
      setValue("locationNormalizedName", loc.display_name, {
        shouldDirty: true,
      });
      setValue("parishName", loc.address?.village || loc.address?.suburb, {
        shouldDirty: true,
      });
      setValue("municipalityName", loc.address?.county, { shouldDirty: true });
      setValue("districtName", loc.address?.city, { shouldDirty: true });
    } catch {
      setValue("locationNormalizedName", portfolio.title, {
        shouldDirty: true,
      });
    }
  }
}
